/**
 * Dashboard TV de estados de entregas en tiempo real
 */

import React, { useState, useEffect } from 'react';
import { Box, Grid, CircularProgress, Alert, Typography, Container, IconButton, Tooltip } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { useEstadoEntregas, useMarquesina } from '../hooks';
import { CardEstadoRuta, Reloj, Marquesina, ResumenEstados } from '../components';

const EstadoEntregasTv = () => {
    const { deliveries, loading: loadingDeliveries, error: errorDeliveries } = useEstadoEntregas();
    const { marquee, marqueeDesp } = useMarquesina();
    const [isFullscreen, setIsFullscreen] = useState(false);

    // Ocultar sidebar cuando está en modo fullscreen
    useEffect(() => {
        const appBody = document.querySelector('.c-app');
        const sidebar = document.querySelector('.c-sidebar');

        if (isFullscreen) {
            // Ocultar sidebar y ajustar layout
            if (sidebar) sidebar.style.display = 'none';
            if (appBody) appBody.style.marginLeft = '0';
        } else {
            // Restaurar sidebar
            if (sidebar) sidebar.style.display = '';
            if (appBody) appBody.style.marginLeft = '';
        }

        // Cleanup al desmontar
        return () => {
            if (sidebar) sidebar.style.display = '';
            if (appBody) appBody.style.marginLeft = '';
        };
    }, [isFullscreen]);

    // Toggle fullscreen
    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    // Loading: mostrar spinner profesional
    if (loadingDeliveries) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                    background: '#f8f9fa'
                }}
            >
                <CircularProgress
                    size={80}
                    thickness={4}
                    sx={{
                        color: '#cc071e',
                        mb: 3
                    }}
                />
                <Typography
                    variant="h5"
                    sx={{
                        color: '#6c757d',
                        fontWeight: 500
                    }}
                >
                    Cargando estados de entregas...
                </Typography>
            </Box>
        );
    }

    // Error: mostrar alert profesional
    if (errorDeliveries) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 4,
                    background: '#f8f9fa'
                }}
            >
                <Alert
                    severity="error"
                    sx={{
                        maxWidth: 600,
                        fontSize: '1.1rem',
                        '& .MuiAlert-icon': {
                            fontSize: 32
                        }
                    }}
                >
                    {errorDeliveries}
                </Alert>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                background: '#f8f9fa',
                overflow: 'hidden',
                ...(isFullscreen && {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 9999
                })
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    background: '#cc071e',
                    boxShadow: '0 4px 20px rgba(204, 7, 30, 0.3)',
                    borderBottom: '4px solid #a30618',
                    flexShrink: 0
                }}
            >
                <Container maxWidth="xl">
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: { xs: 'stretch', md: 'center' },
                            gap: { xs: 1, md: 1.5 },
                            py: { xs: 1, md: 1.5 }
                        }}
                    >
                        {/* Fila principal: Icono + Título + Botón fullscreen */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: { xs: 2, md: 3 },
                                flex: 1
                            }}
                        >
                            <DashboardIcon
                                sx={{
                                    fontSize: { xs: 32, md: 40 },
                                    color: 'white',
                                    display: { xs: 'none', sm: 'block' }
                                }}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    variant="h3"
                                    sx={{
                                        color: 'white',
                                        fontWeight: 800,
                                        letterSpacing: 1.5,
                                        textTransform: 'uppercase',
                                        textShadow: '2px 2px 4px rgba(0,0,0,0.2)',
                                        mb: 0,
                                        fontSize: { xs: '2rem', sm: '2.5rem', md: '2.8rem' },
                                        lineHeight: 1.2
                                    }}
                                >
                                    ESTADO DE PEDIDOS
                                </Typography>
                            </Box>
                            <Tooltip title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}>
                                <IconButton
                                    onClick={toggleFullscreen}
                                    sx={{
                                        color: 'white',
                                        backgroundColor: 'rgba(255,255,255,0.1)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255,255,255,0.2)'
                                        }
                                    }}
                                >
                                    {isFullscreen ? (
                                        <FullscreenExitIcon sx={{ fontSize: { xs: 28, md: 32 } }} />
                                    ) : (
                                        <FullscreenIcon sx={{ fontSize: { xs: 28, md: 32 } }} />
                                    )}
                                </IconButton>
                            </Tooltip>
                        </Box>

                        {/* Reloj - en nueva línea en móvil */}
                        <Box
                            sx={{
                                display: { xs: 'flex', md: 'block' },
                                justifyContent: { xs: 'center', md: 'flex-end' },
                                alignItems: 'center'
                            }}
                        >
                            <Reloj compact />
                        </Box>
                    </Box>
                </Container>
            </Box>

            <Box
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <Container
                    maxWidth="xl"
                    sx={{
                        py: { xs: 1.5, md: 2 },
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0
                    }}
                >
                    {/* Resumen general de todos los estados */}
                    <Box sx={{ flexShrink: 0, mb: { xs: 1.5, md: 2 } }}>
                        <ResumenEstados deliveries={deliveries} compact />
                    </Box>

                    {/* Cards de estados de rutas */}
                    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', pb: 1 }}>
                        <Grid
                            container
                            spacing={{ xs: 1.5, sm: 2, md: 2 }}
                        >
                            {deliveries.map((delivery, index) => (
                                <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    md={4}
                                    key={index}
                                >
                                    <CardEstadoRuta delivery={delivery} compact />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>

                    {/* Marquesina (solo si hay texto configurado) */}
                    {marquee && (
                        <Box sx={{ flexShrink: 0, mt: { xs: 0.5, md: 1.5 } }}>
                            <Marquesina
                                text={marquee}
                                isFixed={marqueeDesp === 'true'}
                            />
                        </Box>
                    )}
                </Container>
            </Box>
        </Box>
    );
};

export default EstadoEntregasTv;
